import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { VariableSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { message, Pagination } from 'antd';
import { load, resetError, filterCallCheckLoads, callCheckLoadsPagination } from '../../../../store/actions/callCheckLoadsActions/callCheckLoads';
import { getContentWidth } from '../../../../store/reducers/callCheckLoads/helper/general';
import { CallCheckLoadsSelectors } from '../../../../store/selectors/callCheckLoadsSelectors';
import Row from './row';
import HeaderRow from './headerRow';
import { createSeparator } from '../../../../store/reducers/callCheckLoads/helper/separator';
import Loading from '../../screen/loading';
import { useClientHubOrder } from '../../../hook/hub/useClientHubOrder';
import Filters from './Filters';
import Toolbar from './Toolbar';
import SpreadsheetDetails from './CallCheckLoadsDetails/spreadsheet';

const Spreadsheet = ({ resource, page }) => {
  const dispatch = useDispatch();
  const { state, loadsAssignedStops, loading, loadDetailsLoading, actionMessage, hasError, total, stateFilterOptions, statePagination, userId } = CallCheckLoadsSelectors();

  const scrollSizeSmall = 16;
  const scrollSize = 16;
  const defRowHeight = 20;
  const assignedRef = useRef({});
  const [uploadModalIsOpen, setUploadModalIsOpen] = useState(false);
  const width = getContentWidth(state[resource].headers) + scrollSize;
  const history = useHistory();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(4);
  const [loadDetailsModalIsOpen, setLoadDetailsModalIsOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);

  useClientHubOrder(message => {
    if (userId !== message.byId) {
      dispatch(load(resource, page, stateFilterOptions, statePagination, 'withoutLoading'));
    }
  }, 'notification');

useEffect(() => {
  return () => {
    dispatch(filterCallCheckLoads(resource, 'searchText', ''));
    dispatch(callCheckLoadsPagination(resource, 'page', ''));
    dispatch(callCheckLoadsPagination(resource, 'pageSize', ''));
  };
}, [dispatch, resource]);

  useEffect(() => {
    dispatch(load(resource, page, stateFilterOptions, statePagination));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, resource, page, statePagination, stateFilterOptions.searchText, dispatch]);
  // DON'T Use stateFilterOptions as dependency as it bring update/call after any filter select before click on Apply

  useEffect(() => {
    if (hasError) {
      message.error(actionMessage);
      dispatch(resetError(resource));
    }
  }, [hasError, actionMessage, resource, dispatch]);

  const listReference = useRef(null);
  const listHeaderReference = useRef(null);
  const mainScroll = useRef(null);
  const startOnScroll = width => (mainScroll.current.scrollLeft = width);

  const onScrollX = e => {
    if (listHeaderReference.current && listReference.current && e.target) {
      listHeaderReference.current.scrollLeft = listReference.current.scrollLeft = e.target.scrollLeft;
    }
  };

  const handleSelectChange = useCallback(
    (value, type) => {
      dispatch(filterCallCheckLoads(resource, type, value));
    },
    [dispatch, resource],
  );

  const handlePagination = useCallback(
    (value, type) => {
      dispatch(callCheckLoadsPagination(resource, type, value));
    },
    [dispatch, resource],
  );

  const handleUpdateCurrentPage = useCallback(page => {
    setCurrentPage(page);
    handlePagination(page, 'page');
  }, [handlePagination]);

  const handleSetPageSize = useCallback(pageSize => {
    setPageSize(pageSize);
    handlePagination(pageSize, 'pageSize');
  }, [handlePagination]);

  useEffect(() => {
    filterCallCheckLoads(resource, 'default');
    history.listen(() => {
      dispatch(filterCallCheckLoads(resource, 'default'));
    });
    return () => {
      dispatch(filterCallCheckLoads(resource, 'default'));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, history]);

  const header = useMemo(
    () => (
      <div style={{ overflow: 'hidden' }}>
        <div
          style={{
            position: 'absolute',
            background: '#449996',
            height: '31px',
            zIndex: '1',
            borderRight: '1px solid #325353',
          }}></div>
        <div
          ref={listHeaderReference}
          style={{
            width: '100%',
            overflow: 'hidden',
          }}>
          <HeaderRow resource={resource} state={state} />
        </div>
      </div>
    ),
    [resource, state],
  );

  const displayLoadsAssignedStops = useMemo(
    () =>
      !loadsAssignedStops || loadsAssignedStops.length === 0
        ? [createSeparator(defRowHeight, state[resource].headers, 'load', 0)]
        : loadsAssignedStops,
    [loadsAssignedStops, state, resource],
  );

  const visibleDisplayLoadsAssignedStops = useMemo(
    () => displayLoadsAssignedStops.filter(order => order.rowState.visible),
    [displayLoadsAssignedStops],
  );

  const getItemSize = useCallback(
    index => {
      const item = visibleDisplayLoadsAssignedStops[index];
      switch (item?.rowState?.type) {
        case 'load':
        case 'dropLoad':
          return 30;
        case 'separator':
          return 10;
        default:
          return 20;
      }
    },
    [visibleDisplayLoadsAssignedStops],
  );

  const RenderAssignedRow = useCallback(
    ({ data, index, style }) =>
      index !== undefined && data[index] ? (
        <Row
          listRef={assignedRef}
          data={data[index]}
          itemSize={getItemSize(index)}
          style={style}
          dndRowIndex={index}
          startOnScroll={startOnScroll}
          resource={resource}
          page={page}
          uploadModalIsOpen={uploadModalIsOpen}
          setUploadModalIsOpen={setUploadModalIsOpen}
          setLoadDetailsModalIsOpen={setLoadDetailsModalIsOpen}
          setCurrentRow={setCurrentRow}
        />
      ) : null,
    [getItemSize, page, resource, uploadModalIsOpen],
  );

  useEffect(() => {
    if (assignedRef.current?.resetAfterIndex) {
      assignedRef.current.resetAfterIndex(0, true); // true = force re-render
    }
  }, [visibleDisplayLoadsAssignedStops]);

  const allOrdersList = useMemo(
    () =>
      visibleDisplayLoadsAssignedStops && (
        <AutoSizer disableWidth>
          {({ height }) => (
            <>
              <VariableSizeList
                ref={assignedRef}
                outerRef={el => {
                  listReference.current = el;
                }}
                style={{ overflowX: 'scroll', overflowY: 'scroll' }}
                itemData={visibleDisplayLoadsAssignedStops}
                itemCount={visibleDisplayLoadsAssignedStops.length}
                height={height}
                itemSize={getItemSize}>
                {RenderAssignedRow}
              </VariableSizeList>
            </>
          )}
        </AutoSizer>
      ),
    [visibleDisplayLoadsAssignedStops, getItemSize, RenderAssignedRow],
  );

  const content = (
    <div className='main-and-filter__container'>
      <Filters
        state={state}
        resource={resource}
        page={page}
        handleSelectChange={handleSelectChange}
        load={load}
        stateFilterOptions={stateFilterOptions}
        statePagination={statePagination}
      />
      <div className='main-data__container'>
        <div style={{ display: 'flex' }}>
          {header}
          <div
            style={{
              minWidth: `${scrollSizeSmall}px`,
              height: '32px',
              backgroundColor: 'lightgrey',
              display: 'flex',
            }}
          />
        </div>
        <div style={{ height: `calc(100% - 105px)`, width: '100%' }}>{allOrdersList}</div>
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={total}
            onChange={page => handleUpdateCurrentPage(page)}
            showSizeChanger={true}
            onShowSizeChange={(current, size) => handleSetPageSize(size)}
            pageSizeOptions={['4', '5', '10', '15', '20']}
            size='small'
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className='call-check-loads__main-container'>
      <div className='call-check-loads__left-container' style={{ minWidth: width+50}}>
        <Toolbar
          state={state}
          resource={resource}
          page={page}
          load={load}
          stateFilterOptions={stateFilterOptions}
          statePagination={statePagination}
          handleSelectChange={handleSelectChange}
        />
        {loading ? <div style={{ width: '800px', height: '500px' }}><Loading /></div> : <>{content}</>}
      </div>
      {loadDetailsModalIsOpen && 
        <div className={`${!loadDetailsLoading ? 'spreadsheet-details__container' : '' }`} style={{width: `calc(100% - 724px)`, height: `calc(100% - 64px)` }}>
          <SpreadsheetDetails 
            resource={resource} 
            rowState={currentRow} 
            page={page}
            setLoadDetailsModalIsOpen={setLoadDetailsModalIsOpen}
        />
        </div>}
    </div>
  );
};

export default Spreadsheet;
